<template>
  <div class="tolerance-checker">
    <div class="tolerance-content-wrapper">
      <div class="tolerance-content">
        <div class="tolerance-rows">
          <ToleranceInputRow
            v-for="field in toleranceFields"
            :key="field.key"
            :field-key="field.key"
            :model-value="field.value"
            :label="field.label"
            :tolerance-options="getToleranceOptionsForField(field)"
            :tolerance-label="i18n.tv('Frontend.Report.Tolerance', 'ORingConfiguratorImport')"
            :min-max-text="field.minMaxText"
            :tolerance-text="field.toleranceText"
            :error-msg="housingValidator.getErrorForField(field.key)"
            @focus="handleFieldFocus(field.key)"
            @update:model-value="(value) => handleToleranceUpdate(field.key, value)"
          />
        </div>

        <!-- Chamfer Angle Input when oringDesignCockpitHasChamfer is true -->
        <div
          v-if="oringDesignCockpitHasChamfer"
          class="chamfer-angle-section"
        >
          <div class="chamfer-angle-input">
            <label
              class="input-label"
              for="chamfer-angle-input"
            >
              {{ i18n.tv('Frontend.Housing.Chamfer', 'ORingConfiguratorImport') }} [°]
            </label>
            <NumericInput
              id="chamfer-angle-input"
              v-model="toleranceFormatter.chamferAngleValue"
              :step="0.1"
              @update:model-value="handleChamferAngleUpdate"
            />
          </div>
        </div>
      </div>

      <div class="tolerance-image">
        <img loading="lazy"
          v-if="selectedImage"
          :src="selectedImage"
          alt="O-Ring Cross Section"
          class="oring-diagram"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {ExpertHousing, FrontendError} from '~/types/oring-api-responses'
import ToleranceInputRow from './ToleranceInputRow.vue'
import NumericInput from '~/components/input-components/NumericInput.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {
  useToleranceDataManager,
  type ToleranceValue
} from '~/composables/oring/useToleranceDataManager'
import {useHousingValidator} from '~/composables/oring/useHousingValidator'
import {useToleranceFormatter} from '~/composables/oring/useToleranceFormatter'
import {useOringImageHelper} from '~/composables/oring/useOringImageHelper'

const i18n = useI18nExtended()

interface Props {
  toleranceData?: Record<string, string | null> // tolerance class IDs by parameter key
  parameterInputData?: Record<string, number | null> // parameter input values by parameter key
}

const props = withDefaults(defineProps<Props>(), {
  toleranceData: () => ({}),
  parameterInputData: () => ({})
})

const emits = defineEmits<{
  'update:toleranceData': [value: Record<string, string | null>]
  'update:parameterInputData': [value: Record<string, number | null>]
  'validation-errors': [errors: FrontendError[]]
  'housing-validated': [housing: ExpertHousing]
  'api-error': [error: any]
}>()

// Use store for tolerance data
const store = useORingCalculatorStore()
const {
  oringDesignCockpitCurrentHousing,
  oringDesignCockpitHasChamfer,
  oringDesignCockpitGeometryId,
  oringCalculatorImperial
} = storeToRefs(store)

// Initialize composables
const toleranceDataManager = useToleranceDataManager()
const housingValidator = useHousingValidator()
const toleranceFormatter = useToleranceFormatter()
const {getImageForGeometryAndKey} = useOringImageHelper()

const focusedField = ref<string | null>(null)

onMounted(async () => {
  await toleranceDataManager.initializeToleranceClasses()
  toleranceDataManager.initializeToleranceDataFromHousing(
    props.toleranceData,
    (data: Record<string, string | null>) => emits('update:toleranceData', data),
    (data: Record<string, number | null>) => emits('update:parameterInputData', data)
  )
  if (props.toleranceData && Object.keys(props.toleranceData).length > 0 && props.parameterInputData && Object.keys(props.parameterInputData).length > 0) {
    checkHousingTimer = setTimeout(() => {
      housingValidator.checkHousing(
        props.toleranceData,
        props.parameterInputData,
        (errors: FrontendError[]) => emits('validation-errors', errors),
        (housing: ExpertHousing) => emits('housing-validated', housing),
        (error: any) => emits('api-error', error)
      )
    }, 500)
  } else {
    await toleranceDataManager.initializeToleranceMinMaxData(props.toleranceData, props.parameterInputData)
  }
  toleranceFormatter.initializeChamferAngleValue()

  // Fetch tolerances for any fields that already have an input value when entering this step
  try {
    await fetchAllTolerancesForExistingValues()
  } catch {
    // ignore errors - non-critical
  }
})

async function fetchAllTolerancesForExistingValues(): Promise<void> {
  const fields = toleranceFields.value
  if (!fields || fields.length === 0) {
    return
  }

  for (const field of fields) {
    const paramValue = field.value.input
    const tolId = field.value.tolerance?.value
    if (paramValue !== null && paramValue !== undefined && tolId) {
      try {
        const toleranceClass = store.toleranceClasses.find(tc => tc.toleranceClassId.toString() === tolId)
        const className = toleranceClass?.toleranceClassName?.toLowerCase() || ''
        const isFree = className.includes('free')
        if (isFree) {
          await toleranceDataManager.callCheckFreeToleranceAPI(
            field.key,
            paramValue,
            tolId,
            field.value.freeToleranceValue,
            field.value.freeToleranceMax,
            field.value.freeToleranceMin
          )
        } else {
          await toleranceDataManager.callGetTolerancesAPI(field.key, paramValue, Number.parseInt(tolId))
        }
      } catch {
        // ignore per-field errors
      }
    }
  }
}

watch(oringCalculatorImperial, () => {
  if (checkHousingTimer) {
    clearTimeout(checkHousingTimer)
  }
  checkHousingTimer = setTimeout(() => {
    housingValidator.checkHousing(
      props.toleranceData,
      props.parameterInputData,
      (errors: FrontendError[]) => emits('validation-errors', errors),
      (housing: ExpertHousing) => emits('housing-validated', housing),
      (error: any) => emits('api-error', error)
    )
  }, 500)
})

watch(oringDesignCockpitCurrentHousing, () => {
  toleranceDataManager.initializeToleranceDataFromHousing(
    props.toleranceData,
    (data: Record<string, string | null>) => emits('update:toleranceData', data),
    (data: Record<string, number | null>) => emits('update:parameterInputData', data)
  )
  toleranceFormatter.initializeChamferAngleValue()
}, {immediate: true})

// Watch for when default tolerances become available and apply them if no tolerance data exists
watch(() => store.oringDesignCockpitDefaultTolerances, () => {
  // Only apply defaults if no tolerance data exists yet
  const hasExistingValues = Object.keys(props.toleranceData).some(key => props.toleranceData[key] !== null)
  if (!hasExistingValues && store.oringDesignCockpitDefaultTolerances) {
    toleranceDataManager.applyDefaultTolerances(
      (data: Record<string, string | null>) => emits('update:toleranceData', data)
    )
  }
}, {immediate: true})

const toleranceFields = computed(() => {
  return toleranceDataManager.generateToleranceFields(
    props.toleranceData,
    props.parameterInputData,
    (key: string) => toleranceFormatter.getParameterMinMaxText(key, toleranceDataManager.toleranceMinMaxData.value),
    (key: string) => toleranceFormatter.getToleranceMinMaxText(key, toleranceDataManager.toleranceMinMaxData.value),
    i18n
  )
})

const selectedImage = computed(() => {
  return getImageForGeometryAndKey(oringDesignCockpitGeometryId.value, focusedField.value)
})

function handleFieldFocus(fieldKey: string) {
  focusedField.value = fieldKey
}

function getToleranceOptionsForField(field: { key: string; categoryId: number }) {
  return toleranceDataManager.getToleranceOptionsByCategory(field.categoryId)
}

let checkHousingTimer: ReturnType<typeof setTimeout> | null = null
let getTolerancesTimer: ReturnType<typeof setTimeout> | null = null

function handleToleranceUpdate(key: string, value: any) {
  // Update local data first
  const toleranceValue = value as ToleranceValue
  const newToleranceData = {...props.toleranceData}
  const newParameterInputData = {...props.parameterInputData}
  if (toleranceValue.tolerance !== undefined) {
    newToleranceData[key] = toleranceValue.tolerance ? toleranceValue.tolerance.value : null
    emits('update:toleranceData', newToleranceData)
  }
  if (toleranceValue.input !== undefined) {
    newParameterInputData[key] = toleranceValue.input
    emits('update:parameterInputData', newParameterInputData)
  }

  // Persist any free tolerance numeric values to the store so they survive step/navigation changes
  if (
    toleranceValue.freeToleranceValue !== undefined ||
    toleranceValue.freeToleranceMax !== undefined ||
    toleranceValue.freeToleranceMin !== undefined
  ) {
    const prev = store.freeToleranceValues && store.freeToleranceValues[key] ? store.freeToleranceValues[key] : {value: null, min: null, max: null}
    const entry = {
      value: toleranceValue.freeToleranceValue === undefined ? (prev.value ?? null) : toleranceValue.freeToleranceValue,
      min: toleranceValue.freeToleranceMin === undefined ? (prev.min ?? null) : toleranceValue.freeToleranceMin,
      max: toleranceValue.freeToleranceMax === undefined ? (prev.max ?? null) : toleranceValue.freeToleranceMax
    }
    store.freeToleranceValues = {...store.freeToleranceValues, [key]: entry}
  }

  if (checkHousingTimer) {
    clearTimeout(checkHousingTimer)
  }
  checkHousingTimer = setTimeout(() => {
    housingValidator.checkHousing(
      newToleranceData,
      newParameterInputData,
      (errors: FrontendError[]) => emits('validation-errors', errors),
      (housing: ExpertHousing) => emits('housing-validated', housing),
      (error: any) => emits('api-error', error)
    )
  }, 500)

  const parameterValue = toleranceValue.input ?? newParameterInputData[key] ?? toleranceDataManager.getParameterValue(key)
  const toleranceClassId = toleranceValue.tolerance?.value ?? newToleranceData[key] ?? toleranceDataManager.getCurrentToleranceClassId(key, newToleranceData)

  if (parameterValue !== null && toleranceClassId) {
    if (getTolerancesTimer) {
      clearTimeout(getTolerancesTimer)
    }
    getTolerancesTimer = setTimeout(() => {
      const toleranceClass = store.toleranceClasses.find(tc => tc.toleranceClassId.toString() === toleranceClassId)
      const className = toleranceClass?.toleranceClassName?.toLowerCase() || ''
      const isFree = className.includes('free')

      if (isFree) {
        toleranceDataManager.callCheckFreeToleranceAPI(
          key,
          parameterValue,
          toleranceClassId,
          toleranceValue.freeToleranceValue,
          toleranceValue.freeToleranceMax,
          toleranceValue.freeToleranceMin
        )
      } else {
        toleranceDataManager.callGetTolerancesAPI(key, parameterValue, parseInt(toleranceClassId))
      }
    }, 500)
  }
}

function handleChamferAngleUpdate() {
  toleranceFormatter.handleChamferAngleUpdate()
}

function validate(): boolean {
  // Check if there are any validation errors from the API
  const hasNoErrors = housingValidator.validate()

  // Check if all required fields are filled
  const fields = toleranceFields.value
  const allFieldsComplete = fields.every(field => {
    // Check if input value is provided
    const hasInput = field.value.input !== null && field.value.input !== undefined

    // Check if tolerance is selected, but only if tolerance options are available
    const toleranceOptions = toleranceDataManager.getToleranceOptionsByCategory(field.categoryId)
    const hasToleranceOptions = toleranceOptions && toleranceOptions.length > 0
    const hasTolerance = hasToleranceOptions
      ? (field.value.tolerance !== null && field.value.tolerance !== undefined)
      : true // If no tolerance options available, consider tolerance as valid

    return hasInput && hasTolerance
  })

  // Check chamfer angle if it's required
  let chamferComplete = true
  if (oringDesignCockpitHasChamfer.value) {
    chamferComplete = toleranceFormatter.chamferAngleValue !== null && toleranceFormatter.chamferAngleValue !== undefined
  }

  return hasNoErrors && allFieldsComplete && chamferComplete
}

defineExpose({validate})
</script>

<style lang="scss" scoped>
.tolerance-checker {
  width: 100%;
  overflow: hidden;
}

.tolerance-content-wrapper {
  display: flex;
  align-items: center;
  gap: 2rem;
  width: 100%;
  min-height: 18.75rem; // Ensure minimum height for image alignment
}

.tolerance-content {
  flex: 0 0 auto;
  max-width: 29.25rem; // 468px
  min-width: 0; // Prevent flex item from growing beyond container
}

.tolerance-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 26.65rem;
}

.tolerance-image {
  flex-shrink: 0;
  width: 18.75rem;
  height: 18.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oring-diagram {
  max-width: 18.75rem;
  max-height: 18.75rem;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.chamfer-angle-section {
  margin-top: 1rem;

  @include breakpoint-down($md) {
    margin-top: 0;
  }
}

.chamfer-angle-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 11.625rem;
}

.input-label {
  font-weight: $base-text-font-weight-light;
}

.tolerance-checker-header {
  margin-bottom: 2rem;
}

@include breakpoint-down($md) {
  .tolerance-checker {
    padding: 0;
    overflow: visible;
  }

  .tolerance-content-wrapper {
    flex-direction: column;
    align-items: stretch; // allow children to take full width
    gap: 1rem;
    min-height: auto;
  }

  .tolerance-image {
    width: 100%;
    height: auto;
    order: -1; // Show image first on mobile
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
    position: sticky; // keep the image visible while scrolling
    top: 3.25rem; // stick to top of viewport
    background-color: $color__white; // white background to hide content underneath
    z-index: 2; // ensure it sits above card contents
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .oring-diagram {
    max-width: 15.625rem;
    max-height: 15.625rem;
  }

  // Make the content container fluid on small screens
  .tolerance-content {
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  // Only in ToleranceChecker: add dividers and spacing between rows
  .tolerance-rows :deep(.tolerance-input-row-outer) {
    border-bottom: 0.125rem solid $color__fst__gray__020; // 2px divider
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  .tolerance-rows :deep(.tolerance-section) {
    margin-top: 0.75rem; // space between input and tolerance area
  }
}

@include breakpoint-down($sm) {
  .tolerance-image {
    margin-bottom: 0.25rem;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
    top: 2.5rem;
  }

  .oring-diagram {
    max-width: 12rem;
    max-height: 12rem;
  }
}
</style>
